import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';

// Use Builder Service API (not legacy webhook proxy)
const BUILDER_API_URL = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';

// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BarChartIcon from '@mui/icons-material/BarChart';
import { ShadowTag, HeaderWithTooltip } from './usageShared';

/**
 * UsagePage - Admin panel for viewing API usage statistics
 *
 * Displays all projects with their usage metrics for billing purposes.
 * Only accessible by admin users.
 */
export default function UsagePage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [startDate, setStartDate] = useState(() => {
    // Default to first day of current month
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    // Default to today
    return new Date().toISOString().split('T')[0];
  });
  const [environment, setEnvironment] = useState('prod');

  // Check if user is admin
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to access the Usage Analytics page.</Alert>
      </Box>
    );
  }

  /**
   * Fetch usage data from the backend API
   */
  const fetchUsageData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Get Firebase Auth token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams({
        startDate,
        endDate,
        environment,
      });

      const response = await fetch(`${BUILDER_API_URL}/api/usage/projects?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // API returns { success, data: [...], meta: {...} }
      setProjects(data.data || []);
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, environment]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  /**
   * Navigate to project details page
   */
  const handleRowClick = (projectId) => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      environment,
    });
    navigate(`/usage/${projectId}?${params}`);
  };

  /**
   * Format number with thousands separator
   */
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  /**
   * Calculate totals for summary
   */
  const totals = projects.reduce((acc, p) => ({
    totalRequests: acc.totalRequests + (p.totalRequests || 0),
    billableRequests: acc.billableRequests + (p.billableRequests || 0),
    verifiedRequests: acc.verifiedRequests + (p.verifiedRequests || 0),
    botRequests: acc.botRequests + (p.botRequests || 0),
    uniqueUsers: acc.uniqueUsers + (p.uniqueUsers || 0),
  }), { totalRequests: 0, billableRequests: 0, verifiedRequests: 0, botRequests: 0, uniqueUsers: 0 });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarChartIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Usage Analytics</Typography>
        </Box>
        <Tooltip title="Refresh data">
          <span>
            <IconButton onClick={fetchUsageData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <TextField
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={environment}
              label="Environment"
              onChange={(e) => setEnvironment(e.target.value)}
            >
              <MenuItem value="prod">Production</MenuItem>
              <MenuItem value="stage">Staging</MenuItem>
              <MenuItem value="dev">Development</MenuItem>
            </Select>
          </FormControl>

          {/* Summary chips */}
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={`Total: ${formatNumber(totals.totalRequests)} requests`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`Billable: ${formatNumber(totals.billableRequests)}`}
            color="success"
            variant="outlined"
          />
          <Chip
            data-testid="usage-chip-verified"
            label={`Verified: ${formatNumber(totals.verifiedRequests)}`}
            color="info"
            variant="outlined"
          />
          <Chip
            data-testid="usage-chip-bots"
            label={`Bots filtered: ${formatNumber(totals.botRequests)}`}
            color="warning"
            variant="outlined"
          />
        </Stack>
      </Paper>

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading state */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        /* Projects table */
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project ID</TableCell>
                  <TableCell align="right">Total Requests</TableCell>
                  <TableCell align="right" data-testid="usage-th-billable">
                    <HeaderWithTooltip
                      label="Billable Impressions"
                      tooltip="Unique visitors per funnel with billable traffic (bots, errors, articles excluded). This is the billed unit."
                    />
                  </TableCell>
                  <TableCell align="right" data-testid="usage-th-verified">
                    <HeaderWithTooltip
                      label="JS-Verified"
                      tooltip="Billable visitors whose browser executed the funnel (client analytics event received). Shadow metric — becomes the billing criterion after reconciliation."
                    >
                      <ShadowTag />
                    </HeaderWithTooltip>
                  </TableCell>
                  <TableCell data-testid="usage-th-filtered">
                    <HeaderWithTooltip
                      label="Filtered Out"
                      tooltip="Requests excluded from billing: bots (ad-network crawlers, headless browsers), errors (4xx/5xx), and article views. Full breakdown on the project details page."
                    />
                  </TableCell>
                  <TableCell align="right">Unique Users</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No usage events reported for the selected period — either there was no
                        traffic, or the usage pipeline is not delivering events for this
                        environment (check /api/usage/debug).
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow
                      key={project.projectId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(project.projectId)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {project.projectId}
                        </Typography>
                        {project.projectName && (
                          <Typography variant="caption" color="text.secondary">
                            {project.projectName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatNumber(project.totalRequests)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatNumber(project.billableRequests)}
                          size="small"
                          color={project.billableRequests > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right" data-testid={`usage-cell-verified-${project.projectId}`}>
                        <Chip
                          label={formatNumber(project.verifiedRequests)}
                          size="small"
                          variant="outlined"
                          color={project.verifiedRequests > 0 ? 'info' : 'default'}
                        />
                        {project.billableRequests > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {Math.round(((project.verifiedRequests || 0) / project.billableRequests) * 100)}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell data-testid={`usage-cell-filtered-${project.projectId}`}>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {`bots ${formatNumber(project.botRequests)} · err ${formatNumber(project.errorRequests)} · art ${formatNumber(project.articleRequests)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(project.uniqueUsers)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(project.lastActivity)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(project.projectId);
                            }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
